import classnames from 'classnames';
import type { FC } from 'react';
import React, { useContext } from 'react';

import {
  RicosDirectionHandledContextProvider,
  RicosNodeContext,
  RicosNodeContextProvider,
  RicosViewerContext,
  VisibleIndentationContext,
} from './../../../../../ricos-context/index.js';
import type { Node } from './../../../../../ricos-schema/legacy/wix/rich_content/v1/index.js';
import { TextStyle_TextAlignment } from './../../../../../ricos-schema/legacy/wix/rich_content/v1/index.js';
import {
  Decoration_Type,
  type NodeViewRenderer,
  type NodeViewRendererProps,
  Node_Type,
} from './../../../../../ricos-types/index.js';
import { getListSymbol, normalizeOffset } from './../../../../../wix-rich-content-common/index.js';

import { getPaywallSeoClass, isPaywallSeo } from '../../utils/paywallSeo.js';
import { levelToClassName } from '../heading/heading.js';
import { getStartMargin } from './getStartMargin.js';
import styles from './lists-scss.js';

const ListItem: FC<NodeViewRendererProps> = ({ node, nodeIndex, RicosNodesRenderer }) => {
  const { seoMode } = useContext(RicosViewerContext);
  const { parentIndex } = useContext(RicosNodeContext);
  const { visibleIndentation } = useContext(VisibleIndentationContext);

  const firstChild = node.nodes[0];
  const dataProperty = `${firstChild?.type?.toLowerCase()}Data`;
  const { textAlignment } =
    (firstChild?.[dataProperty as $TSFixMeKey<Node>] as $TSFixMe)?.textStyle || {};
  const listStylePosition = textAlignment && textAlignment === TextStyle_TextAlignment.CENTER;
  const { parentNode } = useContext(RicosNodeContext);

  const indentation =
    visibleIndentation && visibleIndentation?.nodeId === parentNode?.id
      ? normalizeOffset(visibleIndentation?.indentation)
      : 0;

  const marginInlineStart = getStartMargin(parentNode, visibleIndentation?.indentation || 0);

  const marker = parentNode ? getListSymbol(indentation, parentNode.type) : '';

  const isParentOfHeading = firstChild?.type === Node_Type.HEADING;
  const headingLevel = isParentOfHeading && firstChild.headingData?.level;

  const listItemClassName = classnames(
    styles.listItem,
    isPaywallSeo(seoMode) && getPaywallSeoClass(seoMode?.paywall || {}, nodeIndex + parentIndex)
  );

  const childFontSize = firstChild?.nodes?.[0]?.textData?.decorations?.find(
    decoration => decoration.type === Decoration_Type.FONT_SIZE
  )?.fontSizeData?.value;

  return (
    <li
      dir="auto"
      aria-level={indentation + 1}
      className={listItemClassName}
      style={{
        textAlign: textAlignment,
        fontSize: childFontSize,
        marginInlineStart,
        listStyleType: marker,
      }}
      {...(headingLevel
        ? {
            'data-heading-level':
              levelToClassName[headingLevel as $TSFixMeKey<typeof levelToClassName>],
          }
        : {})}
      {...(listStylePosition ? { 'data-list-style-position': 'inside' } : {})}
    >
      <RicosDirectionHandledContextProvider directionHandled>
        <RicosNodeContextProvider parentNode={node} parentIndex={nodeIndex}>
          <RicosNodesRenderer nodes={node.nodes ?? []} shouldParagraphApplyTextStyle />
        </RicosNodeContextProvider>
      </RicosDirectionHandledContextProvider>
    </li>
  );
};

const ListItemRenderer: NodeViewRenderer = ({ node, ...props }) => {
  return <ListItem key={node.id} node={node} {...props} />;
};

export default ListItemRenderer;
